import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Linkedin, Twitter, Github, Youtube, Instagram, Mail, MapPin, Heart, ArrowUp, Rose } from 'lucide-react'
import profile from '../data/profile.json'

const socialLinks = [
  { name: 'LinkedIn', icon: Linkedin, href: profile.social.linkedin, color: 'hover:text-blue-600' },
  { name: 'Twitter', icon: Twitter, href: profile.social.twitter, color: 'hover:text-sky-500' },
  { name: 'GitHub', icon: Github, href: profile.social.github, color: 'hover:text-gray-900' },
  { name: 'YouTube', icon: Youtube, href: profile.social.youtube, color: 'hover:text-red-600' },
  { name: 'Instagram', icon: Instagram, href: profile.social.instagram, color: 'hover:text-pink-600' },
]

const footerLinks = [
  {
    title: 'Navigation',
    links: [
      { name: 'Accueil', href: '/' },
      { name: 'À propos', href: '/#about' },
      { name: 'Écosystème', href: '/#ecosystem' },
    ],
  },
  {
    title: 'Contenu',
    links: [
      { name: 'Podcast', href: '/podcast' },
      { name: 'Articles', href: '/articles' },
      { name: 'Newsletter', href: 'https://scaltify.com/newsletter', external: true },
    ],
  },
  {
    title: 'Entreprises',
    links: [
      { name: 'Scaltify', href: 'https://scaltify.com', external: true },
      { name: 'Scalt', href: 'https://scalt.ai', external: true },
    ],
  },
]

export default function Footer() {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const currentYear = new Date().getFullYear()

  return (
    <footer id="contact" className="relative bg-gray-50 border-t border-gray-100">
      {/* Main Footer */}
      <div className="px-4 py-16 mx-auto max-w-7xl sm:px-6 lg:px-8 lg:py-20">
        <div className="grid gap-12 lg:grid-cols-4">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center space-x-2">
              <div className="flex items-center justify-center w-10 h-10 font-bold text-white rounded-xl bg-gradient-to-br from-primary-600 to-rose-valley-500 font-display">
                A
              </div>
              <span className="text-lg font-semibold text-gray-900">
                Abder<span className="gradient-text">.</span>
              </span>
            </Link>
            
            <p className="mt-4 text-sm text-gray-600 leading-relaxed">
              {profile.identity.tagline}
            </p>

            <div className="flex items-center gap-2 mt-4 text-sm text-gray-500">
              <MapPin className="w-4 h-4 text-rose-valley-500" />
              <span>{profile.identity.origin.place}, {profile.identity.origin.country}</span>
            </div>

            {/* Social Links */}
            <div className="flex items-center gap-3 mt-6">
              {socialLinks.map((social) => {
                const Icon = social.icon
                return (
                  <motion.a
                    key={social.name}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.1, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                    className={`flex items-center justify-center w-10 h-10 transition-colors rounded-full bg-gray-100 text-gray-500 ${social.color}`}
                    title={social.name}
                  >
                    <Icon className="w-5 h-5" />
                  </motion.a>
                )
              })}
            </div>
          </div>

          {/* Links Columns */}
          {footerLinks.map((group) => (
            <div key={group.title}>
              <h3 className="text-sm font-semibold tracking-wider text-gray-900 uppercase">
                {group.title}
              </h3>
              <ul className="mt-4 space-y-3">
                {group.links.map((link) => (
                  <li key={link.name}>
                    {link.external ? (
                      <a
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-gray-600 transition-colors hover:text-primary-600"
                      >
                        {link.name}
                      </a>
                    ) : (
                      <Link
                        to={link.href}
                        className="text-sm text-gray-600 transition-colors hover:text-primary-600"
                      >
                        {link.name}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Contact CTA */}
        <div className="mt-16 p-8 rounded-2xl bg-gradient-to-r from-primary-50 to-rose-valley-50 border border-primary-100">
          <div className="flex flex-col items-center justify-between gap-6 md:flex-row">
            <div>
              <h3 className="text-xl font-bold text-gray-900">Une question ? Un projet ?</h3>
              <p className="mt-1 text-gray-600">N'hésitez pas à me contacter pour discuter de vos idées.</p>
            </div>
            <motion.a
              href={`mailto:${profile.contact.email}`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="inline-flex items-center gap-2 btn-primary"
            >
              <Mail className="w-4 h-4" />
              Me contacter
            </motion.a>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-200">
        <div className="px-4 py-6 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
            <p className="text-sm text-gray-500">
              © {currentYear} {profile.identity.name}. Tous droits réservés.
            </p>
            <div className="flex items-center gap-1 text-sm text-gray-500">
              <span>Fait avec</span>
              <Heart className="w-4 h-4 text-rose-valley-500" />
              <span>depuis la vallée des roses</span>
              <Rose className="w-4 h-4 text-rose-valley-500" />
              <span className="hidden sm:inline text-gray-300">|</span>
              <span className="font-medium text-gray-700">ⴰⵎⴰⵣⵉⵕ 🇲🇦 Dima Maghrib</span>
            </div>
            <motion.button
              onClick={scrollToTop}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className="flex items-center justify-center w-10 h-10 transition-colors border rounded-full text-gray-500 border-gray-200 hover:border-primary-300 hover:text-primary-600"
              aria-label="Retour en haut"
            >
              <ArrowUp className="w-5 h-5" />
            </motion.button>
          </div>
        </div>
      </div>
    </footer>
  )
}
